import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../css/UserGuestRegistration.css';
import { GoldLoader } from '../shared/Loaders';
import { formatPhoneDisplay } from '../../utils/formUtils';
import { getAuthItem } from '../../utils/authStorage';

const API_URL = process.env.REACT_APP_API_URL || '';
const REGISTRATION_FORM_ID = '68c7eefbd44999eedb541043';
const GUEST_FORM_ID = '68e785cb8e1384b2c283c3c5';

function guestDisplayName(g) {
  const fromParts = [g.firstName, g.lastName].filter(Boolean).join(' ').trim();
  if (fromParts) return fromParts;
  if (g.meta?.fullName) return String(g.meta.fullName).trim();
  if (g.meta?.firstName || g.meta?.lastName) {
    return [g.meta.firstName, g.meta.lastName].filter(Boolean).join(' ').trim();
  }
  return g.email || 'Unnamed guest';
}

function guestPhoneDisplay(g) {
  return (
    formatPhoneDisplay(g.phone_number) ||
    formatPhoneDisplay(g.meta?.phoneNumber) ||
    '—'
  );
}

/**
 * Lists guests already added for the logged-in participant.
 * Click opens Personal Registration (same UserFormView) with that guest's data loaded.
 */
const UserGuestRegistration = () => {
  const navigate = useNavigate();
  const personId = getAuthItem('personId');

  const [participantId, setParticipantId] = useState(null);
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!personId) {
        setError('No person linked to this account.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const participantRes = await axios.get(`${API_URL}/ems/participants`, {
          params: { personId },
        });
        const records = participantRes.data || [];
        const record = records[0];
        if (!record?._id) {
          if (!cancelled) {
            setParticipantId(null);
            setGuests([]);
            setError('No participant record found for your account.');
          }
          return;
        }

        const guestsRes = await axios.get(
          `${API_URL}/ems/participants/${record._id}/guests`
        );
        if (cancelled) return;
        setParticipantId(record._id);
        setGuests(Array.isArray(guestsRes.data) ? guestsRes.data : []);
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.message || 'Failed to load guests');
          setGuests([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [personId]);

  const openGuestInPersonalRegistration = (guestId) => {
    if (!participantId || !guestId) return;
    const qs = new URLSearchParams({
      guestId: String(guestId),
      hostParticipantId: String(participantId),
    });
    navigate(`/user/form/view/registration/${REGISTRATION_FORM_ID}?${qs.toString()}`);
  };

  if (loading) {
    return <GoldLoader label="Loading..." variant="page" />;
  }

  return (
    <div className="user-guest-reg">
      <h1 className="user-guest-reg__title">Guest Registration</h1>
      <p className="user-guest-reg__subtitle">
        Guests already added for your participant. Click a guest to open their
        details in Personal Registration.
      </p>

      <div className="user-guest-reg__table-wrap">
        <div className="user-guest-reg__table-header" aria-hidden="true">
          <div>Name</div>
          <div>Email</div>
          <div>Phone</div>
        </div>

        {loading ? (
          <GoldLoader label="Loading..." variant="section" />
        ) : error ? (
          <div className="user-guest-reg__state user-guest-reg__state--error" role="alert">
            {error}
          </div>
        ) : guests.length === 0 ? (
          <div className="user-guest-reg__state">
            No guests added yet.
            <button
              type="button"
              className="user-guest-reg__link-btn"
              onClick={() =>
                navigate(`/user/form/view/guest/${GUEST_FORM_ID}`)
              }
            >
              Add guests in Personal Registration
            </button>
            .
          </div>
        ) : (
          guests.map((g) => (
            <button
              key={g._id}
              type="button"
              className="user-guest-reg__row"
              onClick={() => openGuestInPersonalRegistration(g._id)}
            >
              <span className="user-guest-reg__name">{guestDisplayName(g)}</span>
              <span>{g.email || g.meta?.email || '—'}</span>
              <span>{guestPhoneDisplay(g)}</span>
            </button>
          ))
        )}
      </div>
    </div>
  );
};

export default UserGuestRegistration;
